import { Checkmark } from '../../../../assets/Checkmark';
import React, { useState } from 'react';
import { T, useTranslate } from '@tolgee/react';
import { MapLegendIcon } from '../../../../assets/MapLegendIcon';
import { ChevronDownIcon } from '../../../../assets/ChevronDownIcon';
import { cn } from '../../../../lib/utils';

const HIDDEN_KEY = 'orderTracking.legendHidden';

const readHidden = (): boolean => {
	try {
		return localStorage.getItem(HIDDEN_KEY) === '1';
	} catch (e) {
		return false;
	}
};

const storeHidden = (hidden: boolean) => {
	try {
		localStorage.setItem(HIDDEN_KEY, hidden ? '1' : '0');
	} catch (e) {
		// a blocked storage only loses the remembered choice
	}
};

const rowClass =
	'flex items-center gap-2 text-[12.5px] text-track-slate whitespace-nowrap';
const swatchClass = 'flex items-center justify-center w-[22px] flex-none';

const Line: React.FC<{ className: string }> = ({ className }) => (
	<span className={swatchClass}>
		<i className={cn('block w-[22px] h-[5px] rounded-full', className)} />
	</span>
);

const Dots: React.FC = () => (
	<span className={cn(swatchClass, 'gap-[3px]')}>
		{[0, 1, 2].map((dot) => (
			<i
				key={dot}
				className="block w-[5px] h-[5px] rounded-full bg-track-amber"
			/>
		))}
	</span>
);

const ActionPin: React.FC = () => (
	<span className={swatchClass}>
		<i className="grid place-items-center w-[16px] h-[16px] rounded-full bg-track-indigo border-2 border-solid border-white shadow-[0_1px_4px_rgba(31,31,39,0.3)]">
			<Checkmark color="#FFFFFF" width={8} height={6} strokeWidth="2.5" />
		</i>
	</span>
);

const Checkpoint: React.FC<{ className: string }> = ({ className }) => (
	<span className={swatchClass}>
		<i
			className={cn(
				'block w-3 h-3 rounded-full border-2 border-solid border-white shadow-[0_1px_4px_rgba(31,31,39,0.3)]',
				className,
			)}
		/>
	</span>
);

interface RouteLegendProps {
	multipleTrucks?: boolean;
}

const RouteLegend: React.FC<RouteLegendProps> = ({
	multipleTrucks = false,
}) => {
	const { t } = useTranslate();
	const [hidden, setHidden] = useState<boolean>(readHidden);

	const toggle = () => {
		setHidden((current) => {
			storeHidden(!current);
			return !current;
		});
	};

	const toggleLabel = hidden
		? t('orderTracking.showLegend', 'Show legend')
		: t('orderTracking.hideLegend', 'Hide legend');

	return (
		<div className="hidden sm:flex flex-col bg-white border border-solid border-track-line rounded-[10px] shadow-[0_2px_12px_rgba(31,31,39,0.08)]">
			<button
				type="button"
				onClick={toggle}
				title={toggleLabel}
				aria-label={toggleLabel}
				aria-expanded={!hidden}
				className="flex items-center gap-2 px-4 py-[10px] bg-transparent border-none cursor-pointer text-[12.5px] font-semibold text-track-ink font-[inherit]"
			>
				<MapLegendIcon />
				<T keyName="orderTracking.legend">Legend</T>
				<span className="ml-auto pl-3 flex items-center">
					<span
						className={cn(
							'flex items-center justify-center w-[10px] h-[10px] transition-transform duration-200',
							hidden && 'rotate-180',
						)}
					>
						<ChevronDownIcon color="#5C6280" />
					</span>
				</span>
			</button>
			{!hidden ? (
				<div className="flex flex-col gap-2 px-4 pb-[14px]">
					<div className={rowClass}>
						<Line className="bg-track-indigo opacity-30" />
						<T keyName="orderTracking.plannedRoute">Planned route</T>
					</div>
					<div className={rowClass}>
						<Line className="bg-track-indigo" />
						{multipleTrucks ? (
							<T keyName="orderTracking.selectedTruckRoute">
								Selected truck route
							</T>
						) : (
							<T keyName="orderTracking.completedRoute">Completed route</T>
						)}
					</div>
					<div className={rowClass}>
						<Dots />
						<T keyName="orderTracking.remainingRoute">Remaining route</T>
					</div>
					<div className={rowClass}>
						<ActionPin />
						<T keyName="orderTracking.checkpointAction">
							Driver action (app position)
						</T>
					</div>
					<div className={rowClass}>
						<Checkpoint className="bg-track-indigo" />
						<T keyName="orderTracking.checkpointGps">
							Checkpoint (GPS position)
						</T>
					</div>
					<div className={rowClass}>
						<Checkpoint className="bg-[#A2A2B5]" />
						<T keyName="orderTracking.checkpointSite">
							Checkpoint (site location)
						</T>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default RouteLegend;
